import {
  buildObservabilityEnvelope,
  isBusinessEvent,
  resolveObservabilityDomain,
  shouldPersistDomain,
  type ObservabilityContext,
  type ObservabilityDomain,
} from "@structura/core/observability";
import {
  auditAction as coreAuditAction,
  captureMetric as coreCaptureMetric,
  capturePlatformEvent as coreCapturePlatformEvent,
  captureTrace as coreCaptureTrace,
  registerPlatformIngest,
} from "@structura/core/platform-runtime";
import { canTransitionOnboardingStatus } from "@structura/core/onboarding-lifecycle";
import { resolveServerReleaseInfo } from "@/lib/release-runtime";
import { sql } from "@/lib/db";
import { nextId } from "@/lib/db.js";

type QueuedError = {
  severity: "error" | "warn";
  envelope: ReturnType<typeof buildObservabilityEnvelope>;
};

type QueuedBusiness = {
  envelope: ReturnType<typeof buildObservabilityEnvelope>;
  entityType?: string;
  entityId?: string;
};

const errorQueue: QueuedError[] = [];
const businessQueue: QueuedBusiness[] = [];
let flushScheduled = false;
const BATCH_MAX = 40;
const FLUSH_MS = 400;

function baseContext(extra?: Partial<ObservabilityContext>): ObservabilityContext {
  const rel = resolveServerReleaseInfo();
  return {
    app: "fm",
    environment: rel.environment ?? process.env.NODE_ENV ?? "development",
    releaseVersion: rel.version,
    releaseChannel: rel.channel,
    releaseCommit: rel.commit,
    tenantId: extra?.tenantId ?? null,
    workspaceId: extra?.workspaceId ?? extra?.tenantId ?? null,
    actorUserId: extra?.actorUserId ?? null,
    requestId: extra?.requestId,
    correlationId: extra?.correlationId,
  };
}

function scheduleFlush(): void {
  if (flushScheduled) return;
  flushScheduled = true;
  setTimeout(() => {
    flushScheduled = false;
    void flushQueues();
  }, FLUSH_MS);
}

async function flushQueues(): Promise<void> {
  const errors = errorQueue.splice(0, BATCH_MAX);
  const business = businessQueue.splice(0, BATCH_MAX);
  if (errors.length === 0 && business.length === 0) return;

  try {
    const db = sql();
    for (const row of errors) {
      const e = row.envelope;
      await db`
        INSERT INTO sm_platform_errors (
          id, severity, event, app, environment, release_version, release_channel,
          tenant_id, workspace_id, actor_user_id, request_id, correlation_id, message, meta
        ) VALUES (
          ${nextId("perr")}, ${row.severity}, ${e.event}, ${e.app}, ${e.environment},
          ${e.releaseVersion}, ${e.releaseChannel},
          ${e.tenantId ?? null}, ${e.workspaceId ?? null}, ${e.actorUserId ?? null},
          ${e.requestId ?? null}, ${e.correlationId ?? null},
          ${typeof e.meta?.message === "string" ? e.meta.message : null},
          ${JSON.stringify(e.meta ?? {})}
        )
      `.catch(() => {});
    }
    for (const row of business) {
      const e = row.envelope;
      await db`
        INSERT INTO sm_business_events (
          id, event, app, environment, release_version, release_channel,
          tenant_id, workspace_id, actor_user_id, entity_type, entity_id, meta
        ) VALUES (
          ${nextId("bevt")}, ${e.event}, ${e.app}, ${e.environment},
          ${e.releaseVersion}, ${e.releaseChannel},
          ${e.tenantId ?? null}, ${e.workspaceId ?? null}, ${e.actorUserId ?? null},
          ${row.entityType ?? null}, ${row.entityId ?? null},
          ${JSON.stringify(e.meta ?? {})}
        )
      `.catch(() => {});
    }
  } catch {
    /* non-blocking */
  }

  if (errorQueue.length > 0 || businessQueue.length > 0) scheduleFlush();
}

function emitConsole(envelope: ReturnType<typeof buildObservabilityEnvelope>): void {
  const line = JSON.stringify(envelope);
  if (envelope.level === "error") console.error(line);
  else if (envelope.level === "warn") console.warn(line);
  else console.info(line);
}

function reportSentry(event: string, envelope: ReturnType<typeof buildObservabilityEnvelope>): void {
  if (envelope.level !== "error") return;
  void import("@/lib/sentry-report")
    .then((m) => {
      m.captureMessage(event, {
        tags: {
          event,
          domain: envelope.domain,
          release: envelope.releaseVersion,
          ...(envelope.tenantId ? { tenantId: String(envelope.tenantId) } : {}),
        },
        extra: envelope.meta,
      });
    })
    .catch(() => {});
}

/** Observabilidad unificada — respeta dominios y política de persistencia. */
export function observe(input: {
  domain?: ObservabilityDomain;
  level: "info" | "warn" | "error";
  event: string;
  ctx?: Partial<ObservabilityContext> & Record<string, unknown>;
  meta?: Record<string, unknown>;
  entityType?: string;
  entityId?: string;
}): void {
  const ctx = baseContext(input.ctx);
  const domain = input.domain ?? resolveObservabilityDomain(input.level, input.event);
  const envelope = buildObservabilityEnvelope({
    domain,
    level: input.level,
    event: input.event,
    ctx,
    meta: { ...input.meta, ...(input.ctx ? stripContextKeys(input.ctx) : {}) },
  });

  emitConsole(envelope);

  if (domain === "debug" && !shouldPersistDomain(domain, input.event, ctx.environment)) return;

  if (domain === "audit") {
    void import("@/lib/db-service/platform")
      .then(({ dbSmAppendAuditLog }) =>
        dbSmAppendAuditLog({
          tenantId: ctx.tenantId ?? "platform",
          action: input.event.replace(/^audit\./, ""),
          entityTable: String(input.meta?.entityTable ?? "platform"),
          entityId: input.meta?.entityId != null ? String(input.meta.entityId) : null,
          actorUserId: ctx.actorUserId ?? null,
          meta: { ...input.meta, requestId: ctx.requestId, release: ctx.releaseVersion },
        }),
      )
      .catch(() => {});
  }

  if (domain === "error" || (domain === "warning" && shouldPersistDomain(domain, input.event, ctx.environment))) {
    errorQueue.push({
      severity: domain === "warning" ? "warn" : "error",
      envelope,
    });
    scheduleFlush();
  }

  if (domain === "business" && isBusinessEvent(input.event)) {
    businessQueue.push({ envelope, entityType: input.entityType, entityId: input.entityId });
    scheduleFlush();
  }

  if (input.level === "error") reportSentry(input.event, envelope);
}

function stripContextKeys(raw: Record<string, unknown>): Record<string, unknown> {
  const keys = new Set([
    "app", "environment", "releaseVersion", "releaseChannel", "tenantId",
    "workspaceId", "actorUserId", "requestId", "correlationId",
  ]);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (!keys.has(k)) out[k] = v;
  }
  return out;
}

/** Evento de negocio allowlisted (onboarding_started, order_created, etc.). */
export function observeBusiness(
  event: string,
  ctx?: Partial<ObservabilityContext>,
  meta?: Record<string, unknown>,
  entity?: { type?: string; id?: string },
): void {
  if (!isBusinessEvent(event)) return;
  observe({
    domain: "business",
    level: "info",
    event,
    ctx,
    meta,
    entityType: entity?.type,
    entityId: entity?.id,
  });
}

export async function dbSmPurgeObservability(retentionDays = 90): Promise<{ errors: number; business: number }> {
  const db = sql();
  const errRows = await db`
    DELETE FROM sm_platform_errors
    WHERE created_at < NOW() - (${retentionDays}::text || ' days')::interval
    RETURNING id
  `;
  const bizRows = await db`
    DELETE FROM sm_business_events
    WHERE created_at < NOW() - (${365}::text || ' days')::interval
    RETURNING id
  `;
  return { errors: errRows.length, business: bizRows.length };
}

registerPlatformIngest((input) => {
  observe({
    domain: input.domain,
    level: input.level,
    event: input.event,
    ctx: input.ctx,
    meta: input.meta,
    entityType: input.entityType,
    entityId: input.entityId,
  });
});

export function auditAction(
  action: string,
  ctx?: Partial<ObservabilityContext>,
  meta?: Record<string, unknown>,
): void {
  coreAuditAction(action, baseContext(ctx), meta);
}

export function capturePlatformEvent(
  input: Omit<Parameters<typeof observe>[0], "ctx"> & { ctx?: Partial<ObservabilityContext> },
): void {
  coreCapturePlatformEvent({ ...input, ctx: baseContext(input.ctx) });
}

export function captureMetric(
  name: string,
  value: number,
  ctx?: Partial<ObservabilityContext>,
  meta?: Record<string, unknown>,
): void {
  coreCaptureMetric(name, value, baseContext(ctx), meta);
}

export function captureTrace(
  name: string,
  ctx?: Partial<ObservabilityContext>,
  meta?: Record<string, unknown> & {
    durationMs?: number;
    failed?: boolean;
    path?: string;
    paymentRelated?: boolean;
  },
): void {
  coreCaptureTrace(name, baseContext(ctx), meta);
}

export { canTransitionOnboardingStatus };
